import React from "react";
import { Plus, Trash2 } from "lucide-react";

import { Button } from "../../../../../../components/button";
import { Input } from "../../../../../../components/input";
import { Label } from "../../../../../../components/label";
import { Select, SelectTrigger, SelectValue } from "../../../../../../components/select";
import { Switch } from "../../../../../../components/switch";
import { FormErrorText } from "../../../../../../components/form/FormErrorText";
import type { QuestionTypeRendererProps } from "./shared";
import type { SurveyQuestion } from "../../../types/survey.types";

type ChoiceQuestionType = "multiple_choice" | "multiple_answer" | "dropdown";

type ChoiceQuestionProps<TType extends ChoiceQuestionType> = QuestionTypeRendererProps<TType> & {
  kind: TType;
};

export function ChoiceQuestion<TType extends ChoiceQuestionType>({
  question,
  isPreview,
  optionErrorMessages,
  onUpdateQuestion,
  kind,
}: ChoiceQuestionProps<TType>) {
  const { data } = question;
  const updateQuestionData = (updates: Partial<typeof data>) => {
    onUpdateQuestion({
      ...question,
      data: {
        ...data,
        ...updates,
      },
    });
  };

  if (isPreview) {
    if (kind === "dropdown") {
      return (
        <Select disabled>
          <SelectTrigger>
            <SelectValue placeholder={data.placeholder || "Select an option"} />
          </SelectTrigger>
        </Select>
      );
    }

    return (
      <div className="space-y-2">
        {(data.options || []).map((option) => (
          <div
            key={option.id}
            className="flex items-center space-x-2"
          >
            <input
              type={kind === "multiple_choice" ? "radio" : "checkbox"}
              disabled
            />
            <span>{option.text}</span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label>Options</Label>
        <Button
          size="sm"
          variant="outline"
          onClick={() =>
            updateQuestionData({
              options: [
                ...(data.options || []),
                {
                  id: `opt${Date.now()}`,
                  text: "",
                  value: "",
                  order: (data.options || []).length + 1,
                  is_other: false,
                },
              ],
            })
          }
        >
          <Plus className="h-4 w-4 mr-1" />
          Add Option
        </Button>
      </div>

      <div className="space-y-2">
        {(data.options || []).map((option, optionIndex) => {
          const optionErrors = optionErrorMessages?.[optionIndex];

          return (
            <div
              key={option.id}
              className="flex items-start gap-2"
            >
              <div className="flex-1">
                <Input
                  value={option.text}
                  onChange={(e) =>
                    updateQuestionData({
                      options: (data.options || []).map((item, currentIndex) =>
                        currentIndex === optionIndex ? { ...item, text: e.target.value } : item,
                      ),
                    })
                  }
                  placeholder="Enter option text"
                  className={`w-full ${optionErrors?.text ? "border-red-500 dark:border-red-500" : ""}`}
                />
                <FormErrorText message={optionErrors?.text} />
              </div>
              <div className="w-32">
                <Input
                  value={option.value}
                  onChange={(e) =>
                    updateQuestionData({
                      options: (data.options || []).map((item, currentIndex) =>
                        currentIndex === optionIndex ? { ...item, value: e.target.value } : item,
                      ),
                    })
                  }
                  placeholder="Enter value"
                  className={optionErrors?.value ? "border-red-500 dark:border-red-500" : ""}
                />
                <FormErrorText message={optionErrors?.value} />
              </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                updateQuestionData({
                  options: (data.options || []).filter(
                    (_, currentIndex) => currentIndex !== optionIndex,
                  ),
                })
              }
              className="text-red-600 hover:text-red-700"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
            </div>
          );
        })}
      </div>

      <div className="flex items-center space-x-2">
        <Switch
          checked={data.allow_other || false}
          onCheckedChange={(checked: boolean) => updateQuestionData({ allow_other: checked })}
        />
        <Label>Allow "Other" option</Label>
      </div>

      {kind === "multiple_answer" && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Min selections</Label>
            <Input
              type="number"
              value={data.min_select || 1}
              onChange={(e) =>
                updateQuestionData({
                  min_select: Number.parseInt(e.target.value, 10) || 1,
                })
              }
              className="mt-1"
            />
          </div>
          <div>
            <Label>Max selections</Label>
            <Input
              type="number"
              value={data.max_select || 3}
              onChange={(e) =>
                updateQuestionData({
                  max_select: Number.parseInt(e.target.value, 10) || 3,
                })
              }
              className="mt-1"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export const validateChoiceQuestion = (question: SurveyQuestion): string | null => {
  const questionText = question.data.question.trim();
  const options = question.data.options || [];

  if (options.length === 0) {
    return `Please add at least one option for question "${questionText}"`;
  }

  for (let optionIndex = 0; optionIndex < options.length; optionIndex += 1) {
    const option = options[optionIndex];

    if (!option.text.trim()) {
      return `Please enter option text for question "${questionText}", option ${optionIndex + 1}`;
    }

    if (!option.value.trim()) {
      return `Please enter option value for question "${questionText}", option ${optionIndex + 1}`;
    }
  }

  return null;
};

export default ChoiceQuestion;
